/**
 * DigestCTA — "Email me this week's Coach recap" pull-back entry point.
 * Queries /digest/preview to decide whether to render (silent if the
 * user has nothing closed out this week). If eligible, shows a small
 * accent card with Preview + Send buttons. Preview opens a modal that
 * renders the email HTML inline; Send dispatches via Resend (or mocks).
 */
import React, { useEffect, useState } from 'react';
import { Mail, X } from 'lucide-react';
import { pieApi } from '../pieApi';
import logger from '../../../lib/logger';

export default function DigestCTA() {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    pieApi
      .digestPreview()
      .then((d) => {
        if (d?.eligible) setPreview(d);
      })
      .catch((e) => logger.warn('digestPreview failed', e));
  }, []);

  if (!preview) return null;

  const onSend = async () => {
    setSending(true);
    setErr(null);
    try {
      const r = await pieApi.digestSend();
      setResult(r);
    } catch (e) {
      logger.error('digestSend failed', e);
      setErr(e?.response?.data?.detail || 'Could not send digest');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div
        className="pie-digest-cta"
        data-testid="pie-digest-cta"
        style={{
          marginBottom: 24,
          padding: '18px 22px',
          background: 'linear-gradient(135deg, rgba(124, 92, 255, 0.08), rgba(6, 182, 212, 0.08))',
          border: '1px solid rgba(124, 92, 255, 0.2)',
          borderRadius: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: 'rgba(124, 92, 255, 0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--pie-accent)',
        }}>
          <Mail size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--pie-text)', marginBottom: 2 }}>
            Your Coach recap is ready
          </div>
          <div style={{ fontSize: 13, color: 'var(--pie-text-secondary)' }}>
            You closed <strong>{preview.count}</strong> loop{preview.count === 1 ? '' : 's'} this week. Want the bundled recap in your inbox?
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="pie-btn pie-btn-ghost"
            onClick={() => setOpen(true)}
            data-testid="pie-digest-preview-btn"
          >
            Preview
          </button>
          <button
            type="button"
            className="pie-btn pie-btn-primary"
            onClick={onSend}
            disabled={sending}
            data-testid="pie-digest-send-btn"
          >
            <Mail size={13} /> {sending ? 'Sending…' : 'Send to my inbox'}
          </button>
        </div>
      </div>

      {open && (
        <DigestPreviewModal
          preview={preview}
          onClose={() => setOpen(false)}
          onSend={onSend}
          sending={sending}
        />
      )}

      {(result || err) && (
        <DigestResultToast
          result={result}
          err={err}
          onDismiss={() => { setResult(null); setErr(null); }}
        />
      )}
    </>
  );
}

function DigestPreviewModal({ preview, onClose, onSend, sending }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="pie-digest-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 50, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 720, maxHeight: '90vh',
          background: 'var(--pie-card-bg, #FFFFFF)', borderRadius: 16,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.3)',
        }}
      >
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--pie-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--pie-accent)', fontWeight: 600, marginBottom: 4 }}>
              Email preview
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--pie-text)' }}>{preview.subject}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="pie-digest-modal-close"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--pie-text-muted)', padding: 6 }}
            aria-label="Close preview"
          >
            <X size={18} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', background: '#F8FAFC' }}>
          <iframe
            title="Digest preview"
            srcDoc={preview.html}
            style={{ width: '100%', height: '60vh', border: 'none', background: '#F8FAFC' }}
            data-testid="pie-digest-iframe"
          />
        </div>
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--pie-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="pie-btn pie-btn-ghost" onClick={onClose}>Close</button>
          <button
            type="button"
            className="pie-btn pie-btn-primary"
            onClick={onSend}
            disabled={sending}
            data-testid="pie-digest-modal-send"
          >
            <Mail size={13} /> {sending ? 'Sending…' : 'Send to my inbox'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DigestResultToast({ result, err, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 6000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  const isErr = !!err;
  const isMocked = result?.delivery?.status === 'mocked';
  const msg = isErr
    ? err
    : isMocked
    ? `Dev mode: email previewed (${result.delivery?.reason || 'no key'}). Real send skipped.`
    : `Sent to ${result?.to}. Check your inbox.`;

  return (
    <div
      role="status"
      data-testid="pie-digest-toast"
      style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 60,
        padding: '12px 18px', borderRadius: 10,
        background: isErr ? '#FEE2E2' : isMocked ? '#FEF3C7' : '#D1FAE5',
        color: isErr ? '#991B1B' : isMocked ? '#92400E' : '#065F46',
        fontSize: 13, fontWeight: 500, maxWidth: 380,
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.15)',
      }}
    >
      {msg}
    </div>
  );
}
